class RoRedirectBaseElement extends HTMLElement {
    constructor() {
        super();

        // Config parameters for the redirect popup

        this.popupId                     = '';                                             // ID of the popup element containing the redirect popup
        this.cookieVersion               = 'v1';                                           // Version of the cookies
        this.originCountryCookieHandle   = `origin_country-${this.cookieVersion}`;         // Cookie handle for storing the user's origin country
        this.selectedCountryCookieHandle = `selected_country-${this.cookieVersion}`;       // Cookie handle for storing the user's selected country
        this.closedPopupCookieHandle     = `closed_redirect_popup-${this.cookieVersion}`;  // Cookie handle for tracking whether the user closed the popup
        this.longCookieDuration          = '60d';                                          // Duration for cookies that should persist for a longer period (h = hours, d = days, m = months, y = years)
        this.shortCookieDuration         = '16h';                                          // Duration for cookies that should expire sooner               (h = hours, d = days, m = months, y = years)
        this.cookieDomain                = '';                                             // Domain for setting cookies
        this.cookieDomain                = this.cookieDomain || window.location.hostname;  // (defaults to the current domain)
        this.hasSeveralStores            = false;                                           // Whether the client has multiple Shopify stores
    }

    connectedCallback() {
        // Initialize the redirect popup

        this.dialogElement          = this.querySelector(`dialog#${this.popupId}`);             // Popup element containing the redirect popup
        this.availableCountries     = window.RICKOWENS.redirectPopup.availableCountries;        // List of available countries and their markets
        this.localizationMarket     = window.RICKOWENS.redirectPopup.localizationMarket;        // Market of the localization (Shopify localization cookie)
        this.originCountry          = null;                                                     // ISO code of the country where the user is located (browsing context suggestions API)
        this.originCountryMarket    = null;                                                     // Market of the user's origin country, based on the user's location
        this.userUrl                = null;                                                     // URL of the current page
    }

    /*
        Geolocalize the user and set the originCountry and originCountryMarket class attributes
    */
    async geolocalizeUser() {
        if (!this.originCountry || !this.originCountryMarket) {
            // Check if the user has a cookie for the origin country
            const originCountryCookie = this.getCookie(this.originCountryCookieHandle);

            if (originCountryCookie) {
                // If the user has a cookie for the origin country, set the originCountry and originCountryMarket
                this.originCountry = originCountryCookie;
                this.originCountryMarket = this.marketFromISO(this.availableCountries, this.originCountry);
                return Promise.resolve();
            } else {
                const self = this;

                // Fetch the user's location from the browsing context suggestions API
                return fetch('/browsing_context_suggestions.json').then(res => res.json()).then((response) => {
                    // ISO code of the country where the user is located
                    self.originCountry = response.detected_values.country.handle;

                    // Store the user's origin country in a cookie
                    self.setCookie(self.originCountryCookieHandle, self.originCountry, self.longCookieDuration, self.cookieDomain);

                    // Get the market of the originCountry
                    self.originCountryMarket = self.marketFromISO(self.availableCountries, self.originCountry);
                });
            }
        }
    }

    /*
        Get the market of a country from its ISO code

        @param {Object} marketData - The data object containing the available countries and their markets
        @param {String} isoCode - The ISO code of the country
        return {String} - The market of the country
    */
    marketFromISO(marketData, isoCode) {
        return marketData.find((country) => { return country.isocode == isoCode })?.market;
    }

    /*
        Show the redirect popup
    */
    showPopup() {}

    /*
        Hide the redirect popup
    */
    hidePopup() {}

    /*
        Update the localization of the user to a given country

        @param {String} selectedCountry - The ISO code of the country to update the localization to
    */
    updateLocalization(selectedCountry) {
        // If we have several stores, we may need to redirect to a separated Shopify store,
        // otherwise we can just update the localization using the Shopify localization API

        if (this.hasSeveralStores) {
            // * Get the market URL of the selected country
            let marketURL = '';

            // For each market, check if the selected country is contained in the list of countries,
            // and if it is, set the market URL to the URL of that market
            const matchingCountryObj = window.RICKOWENS.redirectPopup?.storesMatching?.find(country => country.iso_code === selectedCountry);

            if (matchingCountryObj) {
                marketURL = matchingCountryObj.store_url;
            } else {
                // Defaults to the rest of the world market
                marketURL = window.RICKOWENS.redirectPopup?.storesMatching?.find(country => country.iso_code === "ROW")?.store_url;
            }

            const currentMarketRootUrl = this.userUrl.origin + Shopify.routes.root.substring(0, Shopify.routes.root.length-1);

            // If the market URL exists and is not the same as the user's URL, do not submit the localization
            if (marketURL && marketURL != currentMarketRootUrl) {
                let currentPath = this.userUrl.href.replace(currentMarketRootUrl, '');

                if (currentPath !== '' && currentPath !== '/') {
                    this.userUrl = new URL(marketURL + currentPath);
                } else {
                    this.userUrl = new URL(marketURL);
                }

                // Add the redirect parameter and the selected country to the URL parameters
                this.userUrl.searchParams.set('redirect', 'true');
                this.userUrl.searchParams.set('selectedCountry', selectedCountry);

                publish(PUB_SUB_EVENTS.marketChange);

                // Redirect the user to the new URL
                window.location.href = this.userUrl.href;
                return;
            }
        }

        // * The store has only this instance OR we're redirecting to the same instance, update the localization using the Shopify localization API

        // Remove URL parameters
        this.userUrl.searchParams.delete('redirect');
        this.userUrl.searchParams.delete('selectedCountry');
        history.replaceState({}, '', this.userUrl.href);

        // Perform a localization update using Shopify localization API
        const formId = crypto.randomUUID();
        const formHtml = `
        <form id="${formId}" action="/localization" method="POST" hidden>
            <input name="_method" value="PUT">
            <input name="country_code" value="${selectedCountry}">
        </form>
        `;
        document.body.insertAdjacentHTML("beforeend", formHtml);
        // Submit the form
        publish(PUB_SUB_EVENTS.marketChange)

        document.getElementById(formId).requestSubmit();
    }

    /*
        Get the value of a cookie given its handle

        @param {String} handle - The handle of the cookie
        return {String} - The value of the cookie
    */
    getCookie(handle) {
        return document.cookie.match(new RegExp(`(^| )${handle}=([^;]+)`))?.at(2);
    }

    /*
        Set the value of a cookie given its handle, value, duration, and domain

        @param {String} name - The name of the cookie
        @param {String} value - The value of the cookie
        @param {String} durationString - The duration of the cookie
        @param {String} domain - The domain of the cookie
    */
    setCookie(name, value, durationString, domain) {
        const expiresIn = this.parseDuration(durationString);
        if (!expiresIn) return; // Invalid duration

        const d = new Date();
        d.setTime(d.getTime() + expiresIn);
        let expires = "expires=" + d.toUTCString();
        document.cookie = name + "=" + value + "; " + expires + "; Domain=" + domain + "; secure" + "; path=/";
    }

    /*
        Parse a duration string (h = hours, d = days, m = months, y = years) into milliseconds

        @param {String} durationString - The duration string to parse
        return {Number} - The duration in milliseconds
    */
    parseDuration(durationString) {
        const match = durationString.match(/(\d+)([hdmy])/);
        if (!match) return null; // Invalid format

        const value = parseInt(match[1], 10);
        const unit = match[2].toLowerCase();

        let multiplier;
        switch (unit) {
            case "h":
                multiplier = 60 * 60 * 1000; // Milliseconds in an hour
                break;
            case "d":
                multiplier = 24 * 60 * 60 * 1000; // Milliseconds in a day
                break;
            case "m":
                multiplier = 30 * 24 * 60 * 60 * 1000; // Milliseconds in a month
                break;
            case "y":
                multiplier = 365 * 24 * 60 * 60 * 1000; // Milliseconds in a year
                break;
            default:
                return null; // Invalid unit
        }

        return value * multiplier;
    }
}

class RoRedirectPopup extends RoRedirectBaseElement {
    constructor() {
        super();

        // Config parameters for the redirect popup

        this.popupId                     = 'redirectPopup';                                // ID of the popup element containing the redirect popup
        this.localizationCountryBtnId    = 'localizationCountryBtn';                       // ID of the button to continue shopping in the localization country market
        this.originCountryBtnId          = 'originCountryBtn';                             // ID of the button to continue shopping in the origin country market
    }

    connectedCallback() {
        // Initialize the redirect popup
        super.connectedCallback();

        this.localizationCountryBtn = this.querySelector(`button#${this.localizationCountryBtnId}`);
        this.originCountryBtn       = this.querySelector(`button#${this.originCountryBtnId}`);

        this.performMarketCheck();  // Perform the market check
    }

    performMarketCheck() {
        this.userUrl = new URL(window.location.href);

        this.geolocalizeUser().then(() => {
            if (this.originCountryMarket == this.localizationMarket) {
                this.setCookie(this.selectedCountryCookieHandle, this.originCountry, this.longCookieDuration, this.cookieDomain);
                return;
            }
            if (!this.getCookie(this.closedPopupCookieHandle)) {
                this.showPopup();
            }
        });
        return;

        /**
         * This avoid weird redirect loops.
         * When multistore will be implemented, this will be need to be refactored.
         */


        // * First, check for URL parameters for redirect=true
        this.userUrl              = new URL(window.location.href);
        const redirect            = this.userUrl.searchParams.get('redirect');
        const countryToBeSelected = this.userUrl.searchParams.get('selectedCountry');
        const selectedCountry     = this.getCookie(this.selectedCountryCookieHandle);  // ISO code of the user's selected country
        let destinationCountry    = '';

        if (redirect) {

            // * The user has been redirected to the selected country
            // Create selected country cookie if it does not exist
            if (countryToBeSelected) {
                this.setCookie(this.selectedCountryCookieHandle, countryToBeSelected, this.longCookieDuration, this.cookieDomain);
            }

            // Create closed popup cookie if it does not exist, so the user does not see the popup again
            const closedPopupCookie = this.getCookie(this.closedPopupCookieHandle);
            if (!closedPopupCookie) {
                this.setCookie(this.closedPopupCookieHandle, 'true', this.shortCookieDuration, this.cookieDomain);
            }

            // Redirect to the selected country (if needed)
            destinationCountry = countryToBeSelected;

        } else {

            // * The user has NOT been redirected
            if (selectedCountry) {

                // * The user has previously selected a country, redirect to that country (if needed)
                destinationCountry = selectedCountry;

            } else {

                // * The user has NOT previously selected a country
                // Geolocalize the user and check the market
                this.geolocalizeUser().then(() => {

                    // Check if the user's origin country matches the localization market
                    if (this.originCountryMarket == this.localizationMarket) {

                        // * The user's origin country market matches the localization market
                        // Just set the selected country cookie to the user's origin country
                        this.setCookie(this.selectedCountryCookieHandle, this.originCountry, this.longCookieDuration, this.cookieDomain);

                        // Redirect to the selected country (if needed)
                        destinationCountry = this.originCountry;

                    } else {

                        // * The user's origin country market does not match the localization market
                        // Show the popup if the user has not closed it before
                        if (!this.getCookie(this.closedPopupCookieHandle)) {
                            this.showPopup();
                        }

                    }
                });
            }
        }

        if (destinationCountry) {
            // Get the value of the Shopify localization cookie
            const localizationCountry = this.getCookie('localization');

            if (destinationCountry != localizationCountry) {
                // If the destination country is different from the localization country, update the localization
                this.updateLocalization(destinationCountry);
            }
        }
    }

    /*
        Show the redirect popup
    */
    showPopup() {
        // Disable scrolling on the body
        document.querySelector('body').style.overflow = 'hidden';

        // Attach listeners to the popup buttons
        this.localizationCountryBtn.addEventListener('click', this.continueToLocalizationCountry.bind(this));
        this.originCountryBtn.addEventListener('click', this.stayInOriginCountry.bind(this));

        // Prevent ESC key from closing the popup
        this.dialogElement.addEventListener('keydown', this.handleEscapeKey);

        // Fill the spans in the description and the origin button with the origin country
        const originCountryName = window.RICKOWENS.redirectPopup?.availableCountries?.find(country => country.isocode === this.originCountry)?.name;
        const originCountryDescSpan = this.querySelector('#originCountryDescSpan');
        const originCountryBtnSpan = this.querySelector('#originCountryBtnSpan');
        originCountryDescSpan.textContent = originCountryName;
        originCountryBtnSpan.textContent = this.originCountry;

        // Show the popup
        this.dialogElement.showModal();
    }

    /*
        Prevent the ESC key from closing the popup (prevent default behavior)

        @param {Event} e - The keydown event
    */
    handleEscapeKey(e) {
        if (e.key === 'Escape') {
            e.preventDefault();
        }
    }

    /*
        Let the user continue shopping in the localization country market updating the cookies accordingly
    */
    continueToLocalizationCountry() {
        // Get the value of the Shopify localization cookie
        const localizationCountry = this.getCookie('localization');

        // Set the selected country cookie to the localization country
        this.setCookie(this.selectedCountryCookieHandle, localizationCountry, this.shortCookieDuration, this.cookieDomain);

        // Hide the popup
        this.hidePopup();
    }

    /*
        Redirect the user to the market corresponding to their origin country
    */
    stayInOriginCountry() {
        // Set the selected country cookie to the user's origin country
        this.setCookie(this.selectedCountryCookieHandle, this.originCountry, this.longCookieDuration, this.cookieDomain);

        // Store that the user closed the popup in a cookie
        this.setCookie(this.closedPopupCookieHandle, 'true', this.shortCookieDuration, this.cookieDomain);

        // Update the localization to the user's origin country
        this.updateLocalization(this.originCountry);
    }

    /*
        Hide the redirect popup
    */
    hidePopup() {
        // Enable scrolling on the body
        document.querySelector('body').style.overflow = 'auto';

        // Close the popup
        this.dialogElement.close();

        // Remove event listeners
        this.localizationCountryBtn.removeEventListener('click', this.continueToLocalizationCountry);
        this.originCountryBtn.removeEventListener('click', this.stayInOriginCountry);
        this.dialogElement.removeEventListener('keydown', this.handleEscapeKey);

        // Store that the user closed the popup in a cookie
        this.setCookie(this.closedPopupCookieHandle, 'true', this.shortCookieDuration, this.cookieDomain);
    }
}

customElements.define('ro-redirect-popup', RoRedirectPopup);



class RoCountrySelector extends RoRedirectBaseElement {
    constructor() {
        super();
        // Config parameters for the country selector

        this.localPosition = this.dataset.localPosition;
        this.popupId       = `${this.localPosition}RoCountrySelector`;  // ID of the popup element containing the country selector
        this.openDialogBtnId = `${this.localPosition}OpenRoCountrySelector`;  // ID of the button to open the country selector
        this.closeDialogBtnId = `${this.localPosition}CloseRoCountrySelector`;  // ID of the button to close the country selector
        this.submitFormBtnId = `${this.localPosition}SubmitRoCountrySelector`;  // ID of the button to submit the country selector form
    }
    
    connectedCallback() {
        // Initialize the country selector
        super.connectedCallback();

        this.openDialogBtn = this.querySelector(`button#${this.openDialogBtnId}`);
        this.closeDialogBtn = this.querySelector(`button#${this.closeDialogBtnId}`);
        this.submitFormBtn = this.querySelector(`input#${this.submitFormBtnId}`);
        this.selectElement = this.querySelector('select');
        this.userUrl = new URL(window.location.href);

        this.openDialogBtn.addEventListener('click', this.showPopup.bind(this));

        this.countries.forEach(country => {
            let option = document.createElement('option');
            option.value = country.iso_code;
            option.text = country.name;

            if (country.iso_code === this.dataset.currentCountry) {
                option.defaultSelected = true;
            }

            this.selectElement.add(option);
        });
    }

    

    showPopup() {
        // Disable scrolling on the body
        document.querySelector('body').style.overflow = 'hidden';

        // Attach listeners to the popup buttons
        this.closeDialogBtn.addEventListener('click', this.hidePopup.bind(this));
        this.submitFormBtn.addEventListener('click', this.submitForm.bind(this));

        // Handle the ESC key to close the popup
        this.dialogElement.addEventListener('keydown', this.handleEscapeKey.bind(this));

        // Show the popup
        this.dialogElement.showModal();
    }

    handleEscapeKey(e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            this.hidePopup();
        }
    }

    hidePopup() {
        // Enable scrolling on the body
        document.querySelector('body').style.overflow = 'auto';

        // Close the popup
        this.dialogElement.close();

        // Remove event listeners
        this.closeDialogBtn.removeEventListener('click', this.hidePopup);
        this.dialogElement.removeEventListener('keydown', this.handleEscapeKey);
        this.submitFormBtn.removeEventListener('click', this.submitForm);
    }

    submitForm() {
        const selectedCountry = this.querySelector('select').value;

        // Geolocalize the user and check the market
        this.geolocalizeUser().then(() => {
            // Get the market of the selected country
            const selectedCountryMarket = this.marketFromISO(this.availableCountries, selectedCountry);

            // Check if the user's origin country matches the localization market
            if (this.originCountryMarket == selectedCountryMarket) {

                // * The user's origin country market matches the localization market
                // Just set the selected country cookie to the user's origin country
                // Set the selected country cookie
                this.setCookie(this.selectedCountryCookieHandle, selectedCountry, this.longCookieDuration, this.cookieDomain);

            } else {
                // Markets don't match, so set the selected country cookie with a shorter duration
                this.setCookie(this.selectedCountryCookieHandle, selectedCountry, this.shortCookieDuration, this.cookieDomain);
            }

            // * Update the localization to the selected country
            // Get the value of the Shopify localization cookie
            const localizationCountry = this.getCookie('localization');

            if (selectedCountry != localizationCountry) {
                // If the destination country is different from the localization country, update the localization
                this.updateLocalization(selectedCountry);
            }

        });

        // Hide the popup
        this.hidePopup();
    }

    get countries(){
        return [ 
            {
                "iso_code": "AF",
                "name": "Afghanistan - EUR"
            },
            {
                "iso_code": "AX",
                "name": "Åland Islands - EUR"
            },
            {
                "iso_code": "AL",
                "name": "Albania - EUR"
            },
            {
                "iso_code": "DZ",
                "name": "Algeria - EUR"
            },
            {
                "iso_code": "AD",
                "name": "Andorra - EUR"
            },
            {
                "iso_code": "AO",
                "name": "Angola - EUR"
            },
            {
                "iso_code": "AI",
                "name": "Anguilla - EUR"
            },
            {
                "iso_code": "AG",
                "name": "Antigua &amp; Barbuda - EUR"
            },
            {
                "iso_code": "AR",
                "name": "Argentina - EUR"
            },
            {
                "iso_code": "AM",
                "name": "Armenia - EUR"
            },
            {
                "iso_code": "AW",
                "name": "Aruba - EUR"
            },
            {
                "iso_code": "AC",
                "name": "Ascension Island - EUR"
            },
            {
                "iso_code": "AU",
                "name": "Australia - AUD"
            },
            {
                "iso_code": "AT",
                "name": "Austria - EUR"
            },
            {
                "iso_code": "AZ",
                "name": "Azerbaijan - EUR"
            },
            {
                "iso_code": "BS",
                "name": "Bahamas - EUR"
            },
            {
                "iso_code": "BH",
                "name": "Bahrain - EUR"
            },
            {
                "iso_code": "BD",
                "name": "Bangladesh - EUR"
            },
            {
                "iso_code": "BB",
                "name": "Barbados - EUR"
            },
            {
                "iso_code": "BY",
                "name": "Belarus - EUR"
            },
            {
                "iso_code": "BE",
                "name": "Belgium - EUR"
            },
            {
                "iso_code": "BZ",
                "name": "Belize - EUR"
            },
            {
                "iso_code": "BJ",
                "name": "Benin - EUR"
            },
            {
                "iso_code": "BM",
                "name": "Bermuda - EUR"
            },
            {
                "iso_code": "BT",
                "name": "Bhutan - EUR"
            },
            {
                "iso_code": "BO",
                "name": "Bolivia - EUR"
            },
            {
                "iso_code": "BA",
                "name": "Bosnia &amp; Herzegovina - EUR"
            },
            {
                "iso_code": "BW",
                "name": "Botswana - EUR"
            },
            {
                "iso_code": "BR",
                "name": "Brazil - EUR"
            },
            {
                "iso_code": "IO",
                "name": "British Indian Ocean Territory - EUR"
            },
            {
                "iso_code": "VG",
                "name": "British Virgin Islands - EUR"
            },
            {
                "iso_code": "BN",
                "name": "Brunei - EUR"
            },
            {
                "iso_code": "BG",
                "name": "Bulgaria - EUR"
            },
            {
                "iso_code": "BF",
                "name": "Burkina Faso - EUR"
            },
            {
                "iso_code": "BI",
                "name": "Burundi - EUR"
            },
            {
                "iso_code": "KH",
                "name": "Cambodia - EUR"
            },
            {
                "iso_code": "CM",
                "name": "Cameroon - EUR"
            },
            {
                "iso_code": "CA",
                "name": "Canada - CAD"
            },
            {
                "iso_code": "CV",
                "name": "Cape Verde - EUR"
            },
            {
                "iso_code": "BQ",
                "name": "Caribbean Netherlands - EUR"
            },
            {
                "iso_code": "KY",
                "name": "Cayman Islands - EUR"
            },
            {
                "iso_code": "CF",
                "name": "Central African Republic - EUR"
            },
            {
                "iso_code": "TD",
                "name": "Chad - EUR"
            },
            {
                "iso_code": "CL",
                "name": "Chile - EUR"
            },
            {
                "iso_code": "CN",
                "name": "China - CNY"
            },
            {
                "iso_code": "CX",
                "name": "Christmas Island - EUR"
            },
            {
                "iso_code": "CC",
                "name": "Cocos (Keeling) Islands - EUR"
            },
            {
                "iso_code": "CO",
                "name": "Colombia - EUR"
            },
            {
                "iso_code": "KM",
                "name": "Comoros - EUR"
            },
            {
                "iso_code": "CG",
                "name": "Congo - Brazzaville - EUR"
            },
            {
                "iso_code": "CD",
                "name": "Congo - Kinshasa - EUR"
            },
            {
                "iso_code": "CK",
                "name": "Cook Islands - EUR"
            },
            {
                "iso_code": "CR",
                "name": "Costa Rica - EUR"
            },
            {
                "iso_code": "CI",
                "name": "Côte d’Ivoire - EUR"
            },
            {
                "iso_code": "HR",
                "name": "Croatia - EUR"
            },
            {
                "iso_code": "CW",
                "name": "Curaçao - EUR"
            },
            {
                "iso_code": "CY",
                "name": "Cyprus - EUR"
            },
            {
                "iso_code": "CZ",
                "name": "Czechia - EUR"
            },
            {
                "iso_code": "DK",
                "name": "Denmark - EUR"
            },
            {
                "iso_code": "DJ",
                "name": "Djibouti - EUR"
            },
            {
                "iso_code": "DM",
                "name": "Dominica - EUR"
            },
            {
                "iso_code": "DO",
                "name": "Dominican Republic - EUR"
            },
            {
                "iso_code": "EC",
                "name": "Ecuador - EUR"
            },
            {
                "iso_code": "EG",
                "name": "Egypt - EUR"
            },
            {
                "iso_code": "SV",
                "name": "El Salvador - EUR"
            },
            {
                "iso_code": "GQ",
                "name": "Equatorial Guinea - EUR"
            },
            {
                "iso_code": "ER",
                "name": "Eritrea - EUR"
            },
            {
                "iso_code": "EE",
                "name": "Estonia - EUR"
            },
            {
                "iso_code": "SZ",
                "name": "Eswatini - EUR"
            },
            {
                "iso_code": "ET",
                "name": "Ethiopia - EUR"
            },
            {
                "iso_code": "FK",
                "name": "Falkland Islands - EUR"
            },
            {
                "iso_code": "FO",
                "name": "Faroe Islands - EUR"
            },
            {
                "iso_code": "FJ",
                "name": "Fiji - EUR"
            },
            {
                "iso_code": "FI",
                "name": "Finland - EUR"
            },
            {
                "iso_code": "FR",
                "name": "France - EUR"
            },
            {
                "iso_code": "GF",
                "name": "French Guiana - EUR"
            },
            {
                "iso_code": "PF",
                "name": "French Polynesia - EUR"
            },
            {
                "iso_code": "TF",
                "name": "French Southern Territories - EUR"
            },
            {
                "iso_code": "GA",
                "name": "Gabon - EUR"
            },
            {
                "iso_code": "GM",
                "name": "Gambia - EUR"
            },
            {
                "iso_code": "GE",
                "name": "Georgia - EUR"
            },
            {
                "iso_code": "DE",
                "name": "Germany - EUR"
            },
            {
                "iso_code": "GH",
                "name": "Ghana - EUR"
            },
            {
                "iso_code": "GI",
                "name": "Gibraltar - EUR"
            },
            {
                "iso_code": "GR",
                "name": "Greece - EUR"
            },
            {
                "iso_code": "GL",
                "name": "Greenland - EUR"
            },
            {
                "iso_code": "GD",
                "name": "Grenada - EUR"
            },
            {
                "iso_code": "GP",
                "name": "Guadeloupe - EUR"
            },
            {
                "iso_code": "GT",
                "name": "Guatemala - EUR"
            },
            {
                "iso_code": "GG",
                "name": "Guernsey - EUR"
            },
            {
                "iso_code": "GN",
                "name": "Guinea - EUR"
            },
            {
                "iso_code": "GW",
                "name": "Guinea-Bissau - EUR"
            },
            {
                "iso_code": "GY",
                "name": "Guyana - EUR"
            },
            {
                "iso_code": "HT",
                "name": "Haiti - EUR"
            },
            {
                "iso_code": "HN",
                "name": "Honduras - EUR"
            },
            {
                "iso_code": "HK",
                "name": "Hong Kong SAR - HKD"
            },
            {
                "iso_code": "HU",
                "name": "Hungary - EUR"
            },
            {
                "iso_code": "IS",
                "name": "Iceland - EUR"
            },
            {
                "iso_code": "IN",
                "name": "India - EUR"
            },
            {
                "iso_code": "ID",
                "name": "Indonesia - EUR"
            },
            {
                "iso_code": "IQ",
                "name": "Iraq - EUR"
            },
            {
                "iso_code": "IE",
                "name": "Ireland - EUR"
            },
            {
                "iso_code": "IM",
                "name": "Isle of Man - EUR"
            },
            {
                "iso_code": "IL",
                "name": "Israel - EUR"
            },
            {
                "iso_code": "IT",
                "name": "Italy - EUR"
            },
            {
                "iso_code": "JM",
                "name": "Jamaica - EUR"
            },
            {
                "iso_code": "JP",
                "name": "Japan - JPY"
            },
            {
                "iso_code": "JE",
                "name": "Jersey - EUR"
            },
            {
                "iso_code": "JO",
                "name": "Jordan - EUR"
            },
            {
                "iso_code": "KZ",
                "name": "Kazakhstan - EUR"
            },
            {
                "iso_code": "KE",
                "name": "Kenya - EUR"
            },
            {
                "iso_code": "KI",
                "name": "Kiribati - EUR"
            },
            {
                "iso_code": "XK",
                "name": "Kosovo - EUR"
            },
            {
                "iso_code": "KW",
                "name": "Kuwait - EUR"
            },
            {
                "iso_code": "KG",
                "name": "Kyrgyzstan - EUR"
            },
            {
                "iso_code": "LA",
                "name": "Laos - EUR"
            },
            {
                "iso_code": "LV",
                "name": "Latvia - EUR"
            },
            {
                "iso_code": "LB",
                "name": "Lebanon - EUR"
            },
            {
                "iso_code": "LS",
                "name": "Lesotho - EUR"
            },
            {
                "iso_code": "LR",
                "name": "Liberia - EUR"
            },
            {
                "iso_code": "LY",
                "name": "Libya - EUR"
            },
            {
                "iso_code": "LI",
                "name": "Liechtenstein - EUR"
            },
            {
                "iso_code": "LT",
                "name": "Lithuania - EUR"
            },
            {
                "iso_code": "LU",
                "name": "Luxembourg - EUR"
            },
            {
                "iso_code": "MO",
                "name": "Macao SAR - EUR"
            },
            {
                "iso_code": "MG",
                "name": "Madagascar - EUR"
            },
            {
                "iso_code": "MW",
                "name": "Malawi - EUR"
            },
            {
                "iso_code": "MY",
                "name": "Malaysia - EUR"
            },
            {
                "iso_code": "MV",
                "name": "Maldives - EUR"
            },
            {
                "iso_code": "ML",
                "name": "Mali - EUR"
            },
            {
                "iso_code": "MT",
                "name": "Malta - EUR"
            },
            {
                "iso_code": "MQ",
                "name": "Martinique - EUR"
            },
            {
                "iso_code": "MR",
                "name": "Mauritania - EUR"
            },
            {
                "iso_code": "MU",
                "name": "Mauritius - EUR"
            },
            {
                "iso_code": "YT",
                "name": "Mayotte - EUR"
            },
            {
                "iso_code": "MX",
                "name": "Mexico - USD"
            },
            {
                "iso_code": "MD",
                "name": "Moldova - EUR"
            },
            {
                "iso_code": "MC",
                "name": "Monaco - EUR"
            },
            {
                "iso_code": "MN",
                "name": "Mongolia - EUR"
            },
            {
                "iso_code": "ME",
                "name": "Montenegro - EUR"
            },
            {
                "iso_code": "MS",
                "name": "Montserrat - EUR"
            },
            {
                "iso_code": "MA",
                "name": "Morocco - EUR"
            },
            {
                "iso_code": "MZ",
                "name": "Mozambique - EUR"
            },
            {
                "iso_code": "MM",
                "name": "Myanmar (Burma) - EUR"
            },
            {
                "iso_code": "NA",
                "name": "Namibia - EUR"
            },
            {
                "iso_code": "NR",
                "name": "Nauru - EUR"
            },
            {
                "iso_code": "NP",
                "name": "Nepal - EUR"
            },
            {
                "iso_code": "NL",
                "name": "Netherlands - EUR"
            },
            {
                "iso_code": "NC",
                "name": "New Caledonia - EUR"
            },
            {
                "iso_code": "NZ",
                "name": "New Zealand - AUD"
            },
            {
                "iso_code": "NI",
                "name": "Nicaragua - EUR"
            },
            {
                "iso_code": "NE",
                "name": "Niger - EUR"
            },
            {
                "iso_code": "NG",
                "name": "Nigeria - EUR"
            },
            {
                "iso_code": "NU",
                "name": "Niue - EUR"
            },
            {
                "iso_code": "NF",
                "name": "Norfolk Island - EUR"
            },
            {
                "iso_code": "MK",
                "name": "North Macedonia - EUR"
            },
            {
                "iso_code": "NO",
                "name": "Norway - EUR"
            },
            {
                "iso_code": "OM",
                "name": "Oman - EUR"
            },
            {
                "iso_code": "PK",
                "name": "Pakistan - EUR"
            },
            {
                "iso_code": "PS",
                "name": "Palestinian Territories - EUR"
            },
            {
                "iso_code": "PA",
                "name": "Panama - EUR"
            },
            {
                "iso_code": "PG",
                "name": "Papua New Guinea - EUR"
            },
            {
                "iso_code": "PY",
                "name": "Paraguay - EUR"
            },
            {
                "iso_code": "PE",
                "name": "Peru - EUR"
            },
            {
                "iso_code": "PH",
                "name": "Philippines - EUR"
            },
            {
                "iso_code": "PN",
                "name": "Pitcairn Islands - EUR"
            },
            {
                "iso_code": "PL",
                "name": "Poland - EUR"
            },
            {
                "iso_code": "PT",
                "name": "Portugal - EUR"
            },
            {
                "iso_code": "QA",
                "name": "Qatar - EUR"
            },
            {
                "iso_code": "RE",
                "name": "Réunion - EUR"
            },
            {
                "iso_code": "RO",
                "name": "Romania - EUR"
            },
            {
                "iso_code": "RU",
                "name": "Russia - EUR"
            },
            {
                "iso_code": "RW",
                "name": "Rwanda - EUR"
            },
            {
                "iso_code": "WS",
                "name": "Samoa - EUR"
            },
            {
                "iso_code": "SM",
                "name": "San Marino - EUR"
            },
            {
                "iso_code": "ST",
                "name": "São Tomé &amp; Príncipe - EUR"
            },
            {
                "iso_code": "SA",
                "name": "Saudi Arabia - EUR"
            },
            {
                "iso_code": "SN",
                "name": "Senegal - EUR"
            },
            {
                "iso_code": "RS",
                "name": "Serbia - EUR"
            },
            {
                "iso_code": "SC",
                "name": "Seychelles - EUR"
            },
            {
                "iso_code": "SL",
                "name": "Sierra Leone - EUR"
            },
            {
                "iso_code": "SG",
                "name": "Singapore - EUR"
            },
            {
                "iso_code": "SX",
                "name": "Sint Maarten - EUR"
            },
            {
                "iso_code": "SK",
                "name": "Slovakia - EUR"
            },
            {
                "iso_code": "SI",
                "name": "Slovenia - EUR"
            },
            {
                "iso_code": "SB",
                "name": "Solomon Islands - EUR"
            },
            {
                "iso_code": "SO",
                "name": "Somalia - EUR"
            },
            {
                "iso_code": "ZA",
                "name": "South Africa - EUR"
            },
            {
                "iso_code": "GS",
                "name": "South Georgia &amp; South Sandwich Islands - EUR"
            },
            {
                "iso_code": "KR",
                "name": "South Korea - KRW"
            },
            {
                "iso_code": "SS",
                "name": "South Sudan - EUR"
            },
            {
                "iso_code": "ES",
                "name": "Spain - EUR"
            },
            {
                "iso_code": "LK",
                "name": "Sri Lanka - EUR"
            },
            {
                "iso_code": "BL",
                "name": "St. Barthélemy - EUR"
            },
            {
                "iso_code": "SH",
                "name": "St. Helena - EUR"
            },
            {
                "iso_code": "KN",
                "name": "St. Kitts &amp; Nevis - EUR"
            },
            {
                "iso_code": "LC",
                "name": "St. Lucia - EUR"
            },
            {
                "iso_code": "MF",
                "name": "St. Martin - EUR"
            },
            {
                "iso_code": "PM",
                "name": "St. Pierre &amp; Miquelon - EUR"
            },
            {
                "iso_code": "VC",
                "name": "St. Vincent &amp; Grenadines - EUR"
            },
            {
                "iso_code": "SD",
                "name": "Sudan - EUR"
            },
            {
                "iso_code": "SR",
                "name": "Suriname - EUR"
            },
            {
                "iso_code": "SJ",
                "name": "Svalbard &amp; Jan Mayen - EUR"
            },
            {
                "iso_code": "SE",
                "name": "Sweden - EUR"
            },
            {
                "iso_code": "CH",
                "name": "Switzerland - CHF"
            },
            {
                "iso_code": "TW",
                "name": "Taiwan - EUR"
            },
            {
                "iso_code": "TJ",
                "name": "Tajikistan - EUR"
            },
            {
                "iso_code": "TZ",
                "name": "Tanzania - EUR"
            },
            {
                "iso_code": "TH",
                "name": "Thailand - EUR"
            },
            {
                "iso_code": "TL",
                "name": "Timor-Leste - EUR"
            },
            {
                "iso_code": "TG",
                "name": "Togo - EUR"
            },
            {
                "iso_code": "TK",
                "name": "Tokelau - EUR"
            },
            {
                "iso_code": "TO",
                "name": "Tonga - EUR"
            },
            {
                "iso_code": "TT",
                "name": "Trinidad &amp; Tobago - EUR"
            },
            {
                "iso_code": "TA",
                "name": "Tristan da Cunha - EUR"
            },
            {
                "iso_code": "TN",
                "name": "Tunisia - EUR"
            },
            {
                "iso_code": "TR",
                "name": "Türkiye - EUR"
            },
            {
                "iso_code": "TM",
                "name": "Turkmenistan - EUR"
            },
            {
                "iso_code": "TC",
                "name": "Turks &amp; Caicos Islands - EUR"
            },
            {
                "iso_code": "TV",
                "name": "Tuvalu - EUR"
            },
            {
                "iso_code": "UM",
                "name": "U.S. Outlying Islands - EUR"
            },
            {
                "iso_code": "UG",
                "name": "Uganda - EUR"
            },
            {
                "iso_code": "UA",
                "name": "Ukraine - EUR"
            },
            {
                "iso_code": "AE",
                "name": "United Arab Emirates - EUR"
            },
            {
                "iso_code": "GB",
                "name": "United Kingdom - GBP"
            },
            {
                "iso_code": "US",
                "name": "United States - USD"
            },
            {
                "iso_code": "UY",
                "name": "Uruguay - EUR"
            },
            {
                "iso_code": "UZ",
                "name": "Uzbekistan - EUR"
            },
            {
                "iso_code": "VU",
                "name": "Vanuatu - EUR"
            },
            {
                "iso_code": "VA",
                "name": "Vatican City - EUR"
            },
            {
                "iso_code": "VE",
                "name": "Venezuela - EUR"
            },
            {
                "iso_code": "VN",
                "name": "Vietnam - EUR"
            },
            {
                "iso_code": "WF",
                "name": "Wallis &amp; Futuna - EUR"
            },
            {
                "iso_code": "EH",
                "name": "Western Sahara - EUR"
            },
            {
                "iso_code": "YE",
                "name": "Yemen - EUR"
            },
            {
                "iso_code": "ZM",
                "name": "Zambia - EUR"
            },
            {
                "iso_code": "ZW",
                "name": "Zimbabwe - EUR"
            }
        ];
    }
}

customElements.define('ro-country-selector', RoCountrySelector);